import React, { useMemo, useState } from "react";
import {
  ArrowRight,
  CalendarCheck2,
  CalendarPlus,
  Check,
  Moon,
  RefreshCcw,
  Sparkles,
  SunMedium,
} from "lucide-react";
import { RESPONSE_CHANGE_OPTIONS, changeResponseLabel } from "@/components/daily/adaptiveScore";
import BodyMassNutritionModule from "@/components/daily/BodyMassNutritionModule";
import CalendarMacroTotals from "@/components/daily/CalendarMacroTotals";
import CompanionCalendar from "@/components/daily/CompanionCalendar";
import { buildCompanionWorkoutDraft } from "@/components/daily/companionWorkoutCalendar";
import { AppCard, AppPageHeader } from "@/components/daily/CompanionAppUi";

const fallbackActions = [
  { label: "Use familiar moderate output", level: "moderate" },
  { label: "Use my sport or recreation", level: "selected-activity" },
  { label: "Use a similar activity", level: "similar-activity" },
  { label: "Use a somatic option", level: "somatic" },
  { label: "Give me a lower-demand option", level: "minimum" },
];

const CalendarSection = ({
  assessment,
  plan,
  checkIn,
  identity,
  calendarEvents,
  userCalendarEvents,
  onUserCalendarEventsChange,
}) => (
  <>
    <CompanionCalendar
      assessment={assessment}
      plan={plan}
      checkIn={checkIn}
      identity={identity}
      allEvents={calendarEvents}
      userEvents={userCalendarEvents}
      onUserEventsChange={onUserCalendarEventsChange}
    />
    <CalendarMacroTotals identity={identity} />
    <BodyMassNutritionModule identity={identity} />
  </>
);

const CompanionPlanPageV2 = ({
  assessment,
  identity,
  checkIn,
  plan,
  activeRecord,
  responseMagnitude,
  setResponseMagnitude,
  feltWorse,
  setFeltWorse,
  calendarEvents,
  userCalendarEvents,
  onUserCalendarEventsChange,
  onAddWorkoutToCalendar,
  onOpenCalendar,
  onRematch,
  onComplete,
  onNavigate,
}) => {
  const [addingWorkout, setAddingWorkout] = useState(false);
  const workoutDraft = useMemo(
    () =>
      buildCompanionWorkoutDraft({
        plan,
        checkIn,
        sourceId: activeRecord?.id || "current-plan",
      }),
    [activeRecord?.id, checkIn, plan],
  );
  const workoutAdded = Boolean(
    workoutDraft && userCalendarEvents.some((event) => event.id === workoutDraft.id),
  );

  const addCurrentWorkout = async () => {
    if (!workoutDraft || !onAddWorkoutToCalendar || workoutAdded) return;
    setAddingWorkout(true);
    try {
      await onAddWorkoutToCalendar(workoutDraft);
    } finally {
      setAddingWorkout(false);
    }
  };

  if (!plan || !activeRecord) {
    return (
      <div data-testid="companion-plan-empty">
        <AppPageHeader
          eyebrow="Plan and calendar"
          title="Your schedule lives here"
          body="Add your regular workouts, sports, recreation, or recovery routines. A current nervous-system check-in lets APOS match what you need now around what you are already doing."
        />
        <AppCard tone="action">
          <h2 className="font-display text-2xl forr-text-ink">Match today’s output</h2>
          <p className="mt-3 text-sm leading-relaxed forr-text-soft">
            Your assessment remains the longer-term baseline. The current check-in determines today’s nervous-system state and relative dose.
          </p>
          <button
            type="button"
            onClick={() => onNavigate("checkIn")}
            className="mt-5 inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium forr-btn-primary"
          >
            Open current nervous-system check-in <ArrowRight size={16} />
          </button>
        </AppCard>
        <CalendarSection
          assessment={assessment}
          plan={plan}
          checkIn={checkIn}
          identity={identity}
          calendarEvents={calendarEvents}
          userCalendarEvents={userCalendarEvents}
          onUserCalendarEventsChange={onUserCalendarEventsChange}
        />
      </div>
    );
  }

  return (
    <div data-testid="companion-plan-page">
      <AppPageHeader
        eyebrow="Today’s nervous-system state match"
        title={plan.immediate.title}
        body="Preparation, the main input, transition, recovery, and observation each have a different job. APOS avoids repeating the same modality under different headings."
        action={
          <button
            type="button"
            onClick={() => onNavigate("checkIn")}
            className="rounded-full px-4 py-2.5 text-xs forr-btn-secondary"
          >
            New check-in
          </button>
        }
      />

      <AppCard tone="action">
        <div className="grid gap-5 md:grid-cols-[1fr_0.38fr]">
          <div>
            <div className="forr-overline">Reported response</div>
            <h2 className="mt-2 font-display text-3xl forr-text-ink">{plan.currentState.system}</h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {plan.currentState.labels.map((label) => (
                <span key={label} className="rounded-full bg-white/75 px-3 py-1.5 text-xs forr-text-teal-deep">
                  {label}
                </span>
              ))}
            </div>
            <p className="mt-5 text-sm leading-relaxed forr-text-soft sm:text-base">
              {plan.stateInterpretation.participantExplanation}
            </p>
            <p className="mt-3 text-sm leading-relaxed forr-text-ink">
              <strong>Current aim:</strong> {plan.stateInterpretation.targetExplanation}
            </p>
          </div>
          <div className="rounded-2xl bg-white/75 p-4 text-sm">
            <div className="text-[10px] uppercase tracking-[0.18em] forr-text-mute">Relative dose</div>
            <div className="mt-2 font-medium forr-text-ink">
              {plan.stateMatch?.finalBandLabel || plan.immediate.intensity}
            </div>
            <p className="mt-3 text-xs leading-relaxed forr-text-mute">
              {plan.stateMatch?.explanation || plan.readiness?.reason}
            </p>
          </div>
        </div>
      </AppCard>

      <div className="mt-4 space-y-4">
        {plan.preparation?.steps?.length > 0 && (
          <AppCard tone="information">
            <div className="forr-overline">Before the main output</div>
            <ol className="mt-4 space-y-3">
              {plan.preparation.steps.map((step, index) => (
                <li key={`${index}-${step}`} className="flex gap-3 text-sm leading-relaxed forr-text-soft">
                  <span className="font-display forr-text-teal-deep">{index + 1}</span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          </AppCard>
        )}

        <AppCard tone="action">
          <div className="forr-overline">Main physiological intervention</div>
          <h2 className="mt-3 font-display text-3xl forr-text-ink">{plan.immediate.title}</h2>
          <p className="mt-2 text-sm font-medium forr-text-teal-deep">{plan.immediate.duration}</p>
          <p className="mt-5 text-base leading-relaxed forr-text-ink">{plan.immediate.instruction}</p>
          {plan.immediate.intensity && (
            <div className="mt-5 rounded-2xl bg-white/70 p-4 text-sm forr-text-soft">
              <strong className="forr-text-ink">Relative intensity:</strong> {plan.immediate.intensity}
            </div>
          )}
          {workoutDraft && (
            <div className="mt-5 flex flex-wrap gap-2">
              {!workoutAdded ? (
                <button
                  type="button"
                  onClick={addCurrentWorkout}
                  disabled={addingWorkout}
                  className="inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-medium forr-btn-primary disabled:opacity-50"
                >
                  <CalendarPlus size={16} /> {addingWorkout ? "Adding…" : "Add this workout to calendar"}
                </button>
              ) : (
                <div className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium forr-text-teal-deep forr-surface-tint">
                  <CalendarCheck2 size={16} /> Added to APOS calendar
                </div>
              )}
              {workoutAdded && (
                <button type="button" onClick={onOpenCalendar} className="rounded-full px-4 py-2.5 text-sm forr-btn-secondary">
                  Open calendar
                </button>
              )}
            </div>
          )}
        </AppCard>

        <AppCard tone="information">
          <div className="forr-overline">Transition and recovery</div>
          <h2 className="mt-2 font-display text-2xl forr-text-ink">{plan.cooldown.title}</h2>
          <p className="mt-3 text-sm leading-relaxed forr-text-soft">{plan.cooldown.instruction}</p>
        </AppCard>

        <div className="grid gap-4 md:grid-cols-2">
          <AppCard tone="information">
            <div className="flex items-center gap-2 forr-overline"><SunMedium size={16} /> Morning support</div>
            <p className="mt-3 text-sm leading-relaxed forr-text-soft">{plan.routines.morning.instruction}</p>
          </AppCard>
          <AppCard tone="information">
            <div className="flex items-center gap-2 forr-overline"><Moon size={16} /> Evening and bedtime</div>
            {plan.routines.bedtime.eveningWalk && (
              <p className="mt-3 text-sm leading-relaxed forr-text-soft">{plan.routines.bedtime.eveningWalk}</p>
            )}
            <p className="mt-3 text-sm leading-relaxed forr-text-soft">{plan.routines.bedtime.instruction}</p>
          </AppCard>
        </div>

        <AppCard>
          <div className="flex items-center gap-2 forr-overline"><RefreshCcw size={16} /> Need another way to do this?</div>
          <p className="mt-3 text-sm leading-relaxed forr-text-soft">
            Keep the physiological aim but change the delivery. APOS can use a familiar moderate output, recreation, a similar activity, somatic work, or a lower-demand option matched to the nervous-system state.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            {fallbackActions.map((action) => (
              <button
                type="button"
                key={action.level}
                onClick={() => onRematch({ fallbackLevel: action.level })}
                className="rounded-full px-4 py-2.5 text-xs forr-btn-secondary"
              >
                {action.label}
              </button>
            ))}
          </div>
        </AppCard>

        {!activeRecord.completed ? (
          <AppCard>
            <div className="forr-overline">After the action</div>
            <h2 className="mt-2 font-display text-2xl forr-text-ink">What changed after the input?</h2>
            <p className="mt-3 text-sm leading-relaxed forr-text-soft">
              Completion is participation, not proof that your nervous system adapted. Record the size of the change you actually noticed so APOS can learn from comparable situations.
            </p>
            <div className="mt-5 grid gap-2 sm:grid-cols-5">
              {RESPONSE_CHANGE_OPTIONS.map((option) => {
                const selected = !feltWorse && responseMagnitude === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      setResponseMagnitude(option.value);
                      setFeltWorse(false);
                    }}
                    className="rounded-2xl p-3 text-left sm:text-center"
                    style={{
                      backgroundColor: selected ? "var(--forr-teal-tint)" : "var(--forr-surface-tint)",
                      border: `1px solid ${selected ? "var(--forr-teal)" : "var(--forr-line-soft)"}`,
                    }}
                  >
                    <span className="block font-display text-xl forr-text-teal-deep">{option.value}</span>
                    <span className="mt-1 block text-[10px] font-medium leading-tight forr-text-ink">{option.label}</span>
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              onClick={() => setFeltWorse((current) => !current)}
              className="mt-4 w-full rounded-2xl p-4 text-left"
              style={{
                backgroundColor: feltWorse ? "#f8e5e2" : "var(--forr-surface-tint)",
                border: `1px solid ${feltWorse ? "#c65353" : "var(--forr-line-soft)"}`,
              }}
            >
              <div className="text-sm font-medium forr-text-ink">This changed things in the wrong direction / felt worse</div>
              <div className="mt-1 text-xs leading-relaxed forr-text-mute">
                Recording a worse response is useful learning. It does not remove participation credit or count as failure.
              </div>
            </button>
            <button
              type="button"
              onClick={onComplete}
              className="mt-6 inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium forr-btn-primary"
            >
              <Check size={16} /> Log completion and response
            </button>
          </AppCard>
        ) : (
          <AppCard tone="information">
            <div className="flex items-start gap-3">
              <Check size={20} className="shrink-0 forr-text-teal-deep" />
              <div>
                <div className="font-medium forr-text-ink">Response observation saved</div>
                <p className="mt-1 text-sm leading-relaxed forr-text-soft">
                  {activeRecord.feltWorse
                    ? "You recorded a change in the wrong direction. APOS can suppress or adjust comparable matches in the future."
                    : `${changeResponseLabel(activeRecord.responseMagnitude)} recorded. This observation can strengthen or change future matching when the context is comparable.`}
                </p>
              </div>
            </div>
          </AppCard>
        )}

        <button
          type="button"
          onClick={() => onNavigate("companion")}
          className="flex w-full items-center justify-between gap-4 rounded-[24px] p-5 text-left text-white"
          style={{ background: "linear-gradient(135deg, var(--forr-teal-deep), var(--forr-teal))" }}
        >
          <div className="flex items-center gap-4">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/15"><Sparkles size={21} /></div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-white/70">Adaptive Companion</div>
              <div className="mt-1 font-display text-xl">Ask why you feel this way or why this suggestion fits</div>
            </div>
          </div>
          <ArrowRight size={19} />
        </button>

        <CalendarSection
          assessment={assessment}
          plan={plan}
          checkIn={checkIn}
          identity={identity}
          calendarEvents={calendarEvents}
          userCalendarEvents={userCalendarEvents}
          onUserCalendarEventsChange={onUserCalendarEventsChange}
        />
      </div>
    </div>
  );
};

export default CompanionPlanPageV2;
